import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Switch,
  Drawer,
  Form,
  Input,
  Space,
  Tooltip,
  Popconfirm,
  Select,
  InputNumber,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import LeaveService from "../../Services/Manage/LeaveService";

function AddLeave() {
  const [data, setData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch leave types
  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const res = await LeaveService.getLeaveType();
      if (res?.status && Array.isArray(res.data)) {
        const mappedData = res.data.map((item) => ({
          key: item.id.toString(),
          leaveType: item.leave_type,
          description: item.description,
          numberOfLeaves: item.no_of_leave,
          status: item.status.toLowerCase() === "active",
          paymentStatus: item.payment_status, // NEW
        }));
        setData(mappedData);
      }
    } catch (error) {
      console.error("Failed to fetch leave types:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  // Toggle active/inactive status
  const toggleStatus = async (key) => {
    const record = data.find((item) => item.key === key);
    if (!record) return;

    const newStatus = !record.status;
    setLoading(true);
    try {
      const payload = {
        leave_type: record.leaveType,
        description: record.description,
        no_of_leave: record.numberOfLeaves,
        status: newStatus ? "active" : "inactive",
        payment_status: record.paymentStatus, // include payment status
      };

      await LeaveService.updateLeaveType(key, payload);
      const newData = data.map((item) =>
        item.key === key ? { ...item, status: newStatus } : item
      );
      setData(newData);
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Drawer open/close
  const openDrawer = () => {
    setEditingRecord(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const openEditDrawer = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      leaveType: record.leaveType,
      description: record.description,
      numberOfLeaves: record.numberOfLeaves,
      status: record.status,
      paymentStatus: record.paymentStatus, // NEW
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  // Save or update leave type
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        leave_type: values.leaveType,
        description: values.description,
        no_of_leave: values.numberOfLeaves,
        status: values.status ? "active" : "inactive",
        payment_status: values.paymentStatus, // NEW
      };

      if (editingRecord) {
        await LeaveService.updateLeaveType(editingRecord.key, payload);
        const updatedData = data.map((item) =>
          item.key === editingRecord.key
            ? {
                ...item,
                leaveType: values.leaveType,
                description: values.description,
                numberOfLeaves: values.numberOfLeaves,
                status: values.status,
                paymentStatus: values.paymentStatus,
              }
            : item
        );
        setData(updatedData);
      } else {
        const res = await LeaveService.createLeaveType(payload);
        if (res?.status && res.data) {
          const newRecord = {
            key: res.data.id.toString(),
            leaveType: res.data.leave_type,
            description: res.data.description,
            numberOfLeaves: res.data.no_of_leave,
            status: res.data.status.toLowerCase() === "active",
            paymentStatus: res.data.payment_status, // NEW
          };
          setData([...data, newRecord]);
        }
      }

      closeDrawer();
    } catch (error) {
      console.error("Failed to save leave type:", error);
    } finally {
      setLoading(false);
    }
  };

  // Delete leave type
  const handleDelete = async (key) => {
    setLoading(true);
    try {
      await LeaveService.deleteLeaveType(key);
      setData(data.filter((item) => item.key !== key));
    } catch (error) {
      console.error("Failed to delete leave type:", error);
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "S.No",
      key: "serial",
      render: (_, __, index) => index + 1,
      width: 60,
      align: "center",
    },
    {
      title: "Leave Type",
      dataIndex: "leaveType",
      key: "leaveType",
      align: "center",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
    },
    {
      title: "No. of Leaves",
      dataIndex: "numberOfLeaves",
      key: "numberOfLeaves",
      align: "center",
    },
    {
      title: "Payment Status", // NEW column
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      align: "center",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => (
        <Switch
          checked={status}
          onChange={() => toggleStatus(record.key)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined style={{ fontSize: 16 }} />}
              onClick={() => openEditDrawer(record)}
              type="default"
              size="middle"
              style={{
                border: "1px solid #1890ff",
                color: "#1890ff",
                backgroundColor: "#fff",
                borderRadius: 6,
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure to delete this leave type?"
              onConfirm={() => handleDelete(record.key)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                icon={<DeleteOutlined style={{ fontSize: 16, color: "#ff4d4f" }} />}
                type="default"
                size="middle"
                style={{
                  border: "1px solid #ff4d4f",
                  color: "#ff4d4f",
                  backgroundColor: "#fff",
                  borderRadius: 6,
                }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredData = searchText
    ? data.filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
    : data;

  return (
    <div>
      <Card style={{ marginBottom: 24, borderRadius: 6 }} bodyStyle={{ padding: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 9,
          }}
        >
          <Input
            id="searchInput"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
            style={{ width: 350 }}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openDrawer}
            style={{ height: 40, marginLeft: 16 }}
          >
            Add
          </Button>
        </div>
      </Card>

      <Card variant="borderless" style={{ marginBottom: 24 }} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          rowKey="key"
          loading={loading}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Drawer
        title={editingRecord ? "Edit Leave Type" : "Add Leave Type"}
        width={window.innerWidth < 768 ? "100%" : 400}
        onClose={closeDrawer}
        open={drawerVisible}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="leaveType"
            label="Leave Type"
            rules={[{ required: true, message: "Please enter leave type" }]}
          >
            <Input placeholder="Enter leave type" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item
            name="numberOfLeaves"
            label="Number of Leaves"
            rules={[
              { required: true, message: "Please enter number of leaves" },
              { type: "number", min: 0, message: "Value must be a non-negative number" },
            ]}
          >
            <InputNumber min={0} placeholder="Enter number of leaves" style={{ width: "100%" }} />
          </Form.Item>

          {/* New Payment Status Field */}
          <Form.Item
            name="paymentStatus"
            label="Payment Status"
            rules={[{ required: true, message: "Please select payment status" }]}
          >
            <Select placeholder="Select payment status">
              <Select.Option value="Paid">Paid</Select.Option>
              <Select.Option value="Unpaid">Unpaid</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={closeDrawer}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingRecord ? "Update" : "Submit"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default AddLeave;
